// v9 cosmology — one uniform cell grid.
//
// The mural is a single integer grid centred at (0,0). Every cell is the
// same world-space size. The centre cell hosts an image; a 7×7 Chebyshev
// shield (48 cells, excluding the centre) is permanently empty; every
// other cell may host a piece, placed by the server via corona insert.
// Spec: .meshkore/docs/architecture/tapestry-algorithm-v9.md and
// .meshkore/docs/architecture/viewport-loading.md.
//
// Pieces always arrive from the wire with `(x, y)` set — the server is
// authoritative. No rank-driven fallback (v7/v8 dead path).

/** World-space side length of one cell. Operator default. */
export const CELL_SIZE = 90;

/** Animation duration for spawn-in. */
export const SPAWN_MS = 1000;

/** Legacy alias retained for any external consumer that still imports it. */
export const NUCLEUS_DIAMETER = CELL_SIZE;

export type DisplayZone = "nucleus" | "oort";

export interface PieceSocials {
  x?: string;
  twitter?: string;
  instagram?: string;
  telegram?: string;
  web?: string;
}

export interface Piece {
  id: string;
  /** Arrival-order index (chronological cold-load order). */
  slot: number;
  imageUrl: string;
  thumbUrl: string;
  mediumUrl: string;
  spawnedAt: number;
  provenanceVerdict?: string;
  kind?: "real" | "demo";
  authorNickname?: string;
  authorBrand?: string;
  authorSlogan?: string;
  authorSocials?: PieceSocials;
  /** Derived from (x,y) === (0,0) — `nucleus` only at the centre. */
  displayZone?: DisplayZone;
  /** Unix seconds — server-authored, used by the lightbox "created" line. */
  createdAt?: number;
  likes?: number;
  dislikes?: number;
  myVote?: -1 | 0 | 1;
  /** Server-authoritative grid coordinate (integer cell x). */
  x?: number;
  /** Server-authoritative grid coordinate (integer cell y). */
  y?: number;
  /** True if this piece has ever occupied the centre cell (0,0). */
  wasCentre?: boolean;
  /** True if the requesting user created this piece. */
  isOwn?: boolean;
  /** Vote-crossing animation — set by `runTick` when a piece climbs the
   *  donut. Cleared after `durationMs`. */
  anim?: PieceAnim;
}

export interface PieceAnim {
  fromX: number;
  fromY: number;
  startMs: number;
  durationMs: number;
  role: "bumper" | "yielder";
}

export interface PlacedPiece extends Piece {
  /** Grid coordinate x (required after placement). Inherited from `Piece.x`
   *  but narrowed from optional to required. */
  x: number;
  /** Grid coordinate y (required after placement). */
  y: number;
  /** World-space pixel centre = x * CELL_SIZE. */
  px: number;
  /** World-space pixel centre = y * CELL_SIZE. */
  py: number;
  /** Tile width (aspect-aware), capped at CELL_SIZE. */
  w: number;
  /** Tile height (aspect-aware), capped at CELL_SIZE. */
  h: number;
  zone: DisplayZone;
}

export interface RawPieceItem {
  id: string;
  imageUrl: string;
  thumbUrl?: string;
  mediumUrl?: string;
  provenanceVerdict?: string;
  kind?: "real" | "demo";
  authorNickname?: string;
  authorBrand?: string;
  authorSlogan?: string;
  authorSocials?: PieceSocials;
  displayZone?: DisplayZone;
  createdAt?: number;
  likes?: number;
  dislikes?: number;
  myVote?: -1 | 0 | 1;
  x?: number;
  y?: number;
  wasCentre?: boolean;
  isOwn?: boolean;
}

/** v9 — every tile is the same size. Count-based scaling retired with
 *  v8 (the grid is uniform; scaling lived in the auto-fit zoom). */
export function pieceSizeFor(_count: number): number {
  return CELL_SIZE;
}

/** Cubic ease-in-out, used by the canvas to interpolate animating pieces. */
export function easeInOutCubic(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  if (t < 0.5) return 4 * t * t * t;
  const f = -2 * t + 2;
  return 1 - (f * f * f) / 2;
}

/** Aspect-aware rectangle dims: long side = `size`, short side scales. */
export function tileDims(size: number, aspect: number): { w: number; h: number } {
  if (!isFinite(aspect) || aspect <= 0) return { w: size, h: size };
  if (aspect >= 1) return { w: size, h: size / aspect };
  return { w: size * aspect, h: size };
}

/** Tag each incoming piece with arrival-order slot + spawn timestamp. */
export function assignSlots(items: readonly RawPieceItem[]): Piece[] {
  const now = typeof performance === "undefined" ? 0 : performance.now();
  return items.map((item, slot) => ({
    id: item.id,
    imageUrl: item.imageUrl,
    thumbUrl: item.thumbUrl || item.imageUrl,
    mediumUrl: item.mediumUrl || item.imageUrl,
    provenanceVerdict: item.provenanceVerdict,
    spawnedAt: now,
    slot,
    kind: item.kind,
    authorNickname: item.authorNickname,
    authorBrand: item.authorBrand,
    authorSlogan: item.authorSlogan,
    authorSocials: item.authorSocials,
    likes: item.likes,
    dislikes: item.dislikes,
    myVote: item.myVote,
    displayZone: item.displayZone,
    createdAt: item.createdAt,
    x: item.x,
    y: item.y,
    wasCentre: item.wasCentre,
    isOwn: item.isOwn,
  }));
}

export type AspectMap = ReadonlyMap<string, number>;

function pieceDims(piece: Piece, size: number, aspects: AspectMap | undefined): {
  w: number;
  h: number;
} {
  const a = aspects?.get(piece.id) ?? 1;
  return tileDims(size, a);
}

/** Resolve every piece to its world position. v9: pieces without `(x,y)`
 *  are dropped (the server promises both on every wire entry). */
export function placePieces(
  pieces: readonly Piece[],
  size: number,
  aspects?: AspectMap,
): PlacedPiece[] {
  const out: PlacedPiece[] = [];
  for (const p of pieces) {
    if (typeof p.x !== "number" || typeof p.y !== "number") continue;
    const x = p.x;
    const y = p.y;
    const zone: DisplayZone = x === 0 && y === 0 ? "nucleus" : "oort";
    const { w, h } = pieceDims(p, size, aspects);
    out.push({
      ...p,
      x,
      y,
      px: x * size,
      py: y * size,
      w,
      h,
      zone,
    });
  }
  return out;
}

/** True iff any piece occupies the centre `(0,0)` cell. */
export function hasNucleusPiece(pieces: readonly Piece[]): boolean {
  for (const p of pieces) {
    if (p.x === 0 && p.y === 0) return true;
  }
  return false;
}

/** World-space side length of one cell. Single value — v9 is uniform. */
export function cellSizeForZone(_zone: DisplayZone, size: number): number {
  return size;
}

/** Stable Map key for `(x, y)`. */
export function cellKey(x: number, y: number): string {
  return `${x},${y}`;
}

// v9 protected-centre shield: 5×5 square (Chebyshev radius 2) excluding
// the origin AND the four shield-corner diagonals (±2, ±2). 20 cells
// permanently empty (carve-out applied 2026-06-13). Mirrors
// `is_protected_center_cell` in apps/api/src/tapestry.rs.
export function isProtectedCenterCell(x: number, y: number): boolean {
  if (x === 0 && y === 0) return false;
  if (Math.abs(x) === 2 && Math.abs(y) === 2) return false;
  return Math.abs(x) <= 2 && Math.abs(y) <= 2;
}

/** O(N) → O(1) per-cell lookup. */
export function buildCellIndex(
  placed: readonly PlacedPiece[],
): Map<string, PlacedPiece> {
  const m = new Map<string, PlacedPiece>();
  for (const p of placed) m.set(cellKey(p.x, p.y), p);
  return m;
}

/** One orthogonal cell step toward the origin. */
export function stepTowardOrigin(
  x: number,
  y: number,
): { x: number; y: number } | null {
  if (x === 0 && y === 0) return null;
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  if (ax >= ay && x !== 0) return { x: x - Math.sign(x), y };
  if (y !== 0) return { x, y: y - Math.sign(y) };
  return { x: x - Math.sign(x), y };
}

/** Vote-score scalar used by the bump arbiter. */
export function scoreOf(p: Piece): number {
  return (p.likes ?? 0) - (p.dislikes ?? 0);
}

// ── Viewport culling ───────────────────────────────────────────────────────

export interface ViewportBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function cullToViewport(
  placed: readonly PlacedPiece[],
  view: ViewportBbox,
  bufferScreens = 1,
): PlacedPiece[] {
  const vw = view.maxX - view.minX;
  const vh = view.maxY - view.minY;
  const pad = Math.max(vw, vh) * bufferScreens;
  const minX = view.minX - pad;
  const minY = view.minY - pad;
  const maxX = view.maxX + pad;
  const maxY = view.maxY + pad;
  const out: PlacedPiece[] = [];
  for (let i = 0; i < placed.length; i += 1) {
    const p = placed[i];
    const hw = p.w / 2;
    const hh = p.h / 2;
    if (p.px + hw < minX) continue;
    if (p.px - hw > maxX) continue;
    if (p.py + hh < minY) continue;
    if (p.py - hh > maxY) continue;
    out.push(p);
  }
  return out;
}

// ── Spawn animation ───────────────────────────────────────────────────────

function easeOut(t: number): number {
  const c = 1 - t;
  return 1 - c * c * c;
}

export function spawnProgress(piece: Piece, now: number): number {
  const t = Math.min(1, Math.max(0, (now - piece.spawnedAt) / SPAWN_MS));
  return easeOut(t);
}

// ── Movement simulation ───────────────────────────────────────────────────

/** Number of sub-ticks per 60 s snapshot — mirrors SNAPSHOT_EVERY_N_TICKS
 *  in apps/api/src/tapestry.rs. */
const SNAPSHOT_SUB_TICKS = 30;

/** Duration of the slide animation triggered by runSingleTick. Must be < 2 000 ms
 *  so the piece lands before the next 2 s client tick fires. */
export const MOVE_ANIM_MS = 1_200;

/** Internal: sort order for movement simulation — likes DESC, ULID ASC. */
function buildOrder(
  positions: Map<string, { x: number; y: number }>,
  likesMap: Map<string, number>,
): string[] {
  return [...positions.keys()].sort((a, b) => {
    const la = likesMap.get(a) ?? 0;
    const lb = likesMap.get(b) ?? 0;
    if (lb !== la) return lb - la;
    return a < b ? -1 : a > b ? 1 : 0;
  });
}

/** Internal: one sub-tick — finds the first piece that can step toward origin
 *  and moves it (or swaps). Returns true if a piece moved. */
function executeSubTick(
  positions: Map<string, { x: number; y: number }>,
  likesMap: Map<string, number>,
  order: string[],
): boolean {
  const cellIndex = new Map<string, string>();
  for (const [id, pos] of positions) cellIndex.set(cellKey(pos.x, pos.y), id);

  for (const id of order) {
    const pos = positions.get(id);
    if (!pos) continue;
    const step = stepTowardOrigin(pos.x, pos.y);
    if (!step) continue;
    const target = isProtectedCenterCell(step.x, step.y) ? { x: 0, y: 0 } : step;
    const tk = cellKey(target.x, target.y);
    const ck = cellKey(pos.x, pos.y);
    const occupantId = cellIndex.get(tk);
    if (!occupantId) {
      cellIndex.delete(ck);
      cellIndex.set(tk, id);
      positions.set(id, { ...target });
      return true;
    }
    const pLikes = likesMap.get(id) ?? 0;
    const oLikes = likesMap.get(occupantId) ?? 0;
    if (pLikes <= oLikes) continue;
    const oldPos = { ...pos };
    cellIndex.set(tk, id);
    cellIndex.set(ck, occupantId);
    positions.set(id, { ...target });
    positions.set(occupantId, oldPos);
    return true;
  }
  return false;
}

function diffPositions(
  pieces: readonly Piece[],
  positions: Map<string, { x: number; y: number }>,
): Map<string, { x: number; y: number }> {
  const moved = new Map<string, { x: number; y: number }>();
  for (const p of pieces) {
    if (typeof p.x !== "number" || typeof p.y !== "number") continue;
    const newPos = positions.get(p.id);
    if (newPos && (newPos.x !== p.x || newPos.y !== p.y)) moved.set(p.id, newPos);
  }
  return moved;
}

/** ONE movement step — mirrors one 2 s client tick. Used by the live interval. */
export function runSingleTick(
  pieces: readonly Piece[],
): Map<string, { x: number; y: number }> {
  if (pieces.length === 0) return new Map();
  const positions = new Map<string, { x: number; y: number }>();
  const likesMap = new Map<string, number>();
  for (const p of pieces) {
    if (typeof p.x === "number" && typeof p.y === "number") {
      positions.set(p.id, { x: p.x, y: p.y });
      likesMap.set(p.id, p.likes ?? 0);
    }
  }
  const order = buildOrder(positions, likesMap);
  executeSubTick(positions, likesMap, order);
  return diffPositions(pieces, positions);
}

/** SNAPSHOT_SUB_TICKS sequential steps — mirrors run_snapshot_simulation on
 *  the server; used for cold-load catch-up. */
export function runTick(
  pieces: readonly Piece[],
): Map<string, { x: number; y: number }> {
  if (pieces.length === 0) return new Map();
  const positions = new Map<string, { x: number; y: number }>();
  const likesMap = new Map<string, number>();
  for (const p of pieces) {
    if (typeof p.x === "number" && typeof p.y === "number") {
      positions.set(p.id, { x: p.x, y: p.y });
      likesMap.set(p.id, p.likes ?? 0);
    }
  }
  const order = buildOrder(positions, likesMap);
  for (let i = 0; i < SNAPSHOT_SUB_TICKS; i++) executeSubTick(positions, likesMap, order);
  return diffPositions(pieces, positions);
}
